import type { ApiFailure } from "@price-rescue/api-contracts";

export type MerchantApiErrorKind =
  "auth" | "access" | "conflict" | "validation" | "rate-limit" | "server" | "network" | "unknown";

export interface MerchantApiErrorPresentation {
  code: string;
  details?: Record<string, unknown> | null;
  kind: MerchantApiErrorKind;
  message: string;
  requestId: string;
  retryable: boolean;
}

export class MerchantApiError extends Error {
  public readonly code: string;
  public readonly details?: Record<string, unknown> | null;
  public readonly kind: MerchantApiErrorKind;
  public readonly requestId: string;
  public readonly retryable: boolean;

  public constructor(presentation: MerchantApiErrorPresentation) {
    super(presentation.message);
    this.name = "MerchantApiError";
    this.code = presentation.code;
    if (presentation.details !== undefined) this.details = presentation.details;
    this.kind = presentation.kind;
    this.requestId = presentation.requestId;
    this.retryable = presentation.retryable;
  }
}

export function toMerchantApiError(failure: ApiFailure): MerchantApiError {
  return new MerchantApiError(toMerchantApiErrorPresentation(failure));
}

export function toMerchantApiErrorPresentation(failure: ApiFailure): MerchantApiErrorPresentation {
  return {
    code: failure.error.code,
    details: failure.error.details,
    kind: errorKindForCode(failure.error.code),
    message: merchantMessageForCode(failure.error.code, failure.error.message),
    requestId: failure.requestId,
    retryable: failure.error.retryable,
  };
}

export function isAuthenticationExpired(error: unknown): boolean {
  return (
    error instanceof MerchantApiError &&
    ["AUTH_EXPIRED", "AUTH_INVALID", "PLATFORM_TOKEN_EXPIRED"].includes(error.code)
  );
}

function errorKindForCode(code: string): MerchantApiErrorKind {
  if (code.startsWith("AUTH_") || code === "PLATFORM_TOKEN_EXPIRED") return "auth";
  if (code.startsWith("ACCESS_")) return "access";
  if (code === "REQUEST_STATE_CONFLICT" || code === "REQUEST_DUPLICATE") return "conflict";
  if (code.startsWith("VALIDATION_")) return "validation";
  if (code.startsWith("PRODUCT_")) return "validation";
  if (code.startsWith("RATE_LIMIT_")) return "rate-limit";
  if (code.startsWith("INTERNAL_")) return "server";
  return "unknown";
}

function merchantMessageForCode(code: string, fallback: string): string {
  if (code.startsWith("AUTH_") || code === "PLATFORM_TOKEN_EXPIRED") {
    return "Your platform session expired. Open the app again from your commerce platform or refresh this page.";
  }

  if (code.startsWith("ACCESS_")) {
    return "You do not have permission to perform this action.";
  }

  if (code === "REQUEST_STATE_CONFLICT") {
    return "This record changed while you were editing. Review the current details before saving.";
  }

  if (code.startsWith("VALIDATION_")) {
    return fallback || "Some fields need attention before this can be saved.";
  }

  if (code === "PRODUCT_NOT_ELIGIBLE") {
    return fallback || "This product cannot receive that eligibility change.";
  }

  if (code.startsWith("RATE_LIMIT_")) {
    return "Too many requests were made in a short time. Wait a moment and try again.";
  }

  if (code.startsWith("INTERNAL_")) {
    return "Price Rescue could not complete that request. Try again in a moment.";
  }

  return fallback || "Price Rescue could not complete that request.";
}
